<script setup>
import { computed, defineProps, defineEmits, ref, watch } from 'vue';
import { useStore } from 'vuex';
import Checkbox from '@/components/elements/checkbox/Checkbox';
import dayjs from 'dayjs';
import TooltipGoalDetail from '@/components/elements/tooltipGoalDetail/TooltipGoalDetail';

const store = useStore();

const goalTaught = computed(() => store.getters.goalTaught);

const goalsSelected = computed(() => {
	const s = store.getters.goalsSelected;
	return (s || []).map((i) => i.id);
});

const props = defineProps([
	'data',
	'foldersSelected',
	'forceChecked',
	'isInCreateGoal',
	'isAdmin',
	'groupCreate',
	'formCreate',
	'isEdit',
]);

const emit = defineEmits([
	'action-clicked',
	'delete-goal',
	'open-add-smart-modal',
	'update-goal',
	'create-goal',
]);

const listSubItems = ref([]);

const syncSubItems = () => {
	const sourceItems = Array.isArray(props.data?.sub_items)
		? props.data.sub_items
		: [];
	const normalizedItems = sourceItems.filter(
		(item) => item?.id || item?.name
	);

	if (props.isEdit) {
		normalizedItems.push({
			id: '',
			name: '',
		});
	}

	listSubItems.value = normalizedItems;
};

watch(
	() => [props.data?.sub_items, props.isEdit],
	() => {
		syncSubItems();
	},
	{
		immediate: true,
		deep: true,
	}
);

const checkStatus = (goal) => {
	if (props.forceChecked) {
		return true;
	}
	return goalsSelected.value.find((i) => +i === +goal.id);
};

const check = (goals, isAll) => {
	store.dispatch('selected', { goals, isAll });
};

const isGoalCheckboxDisabled = (goal) =>
	Boolean(goal?.has_completed_goal) ||
	((props.foldersSelected?.length ?? 0) > 0 && !props.forceChecked);

const checkChanged = (goals, isAll) => {
	if (props.forceChecked) {
		return;
	}
	check(goals, isAll);
};

const deleteGoal = (data) => {
	emit('delete-goal', data);
};

const openAddSmartModal = (data) => {
	emit('open-add-smart-modal', data);
};

const updateGoal = (data) => {
	emit('update-goal', data);
};

const createGoal = (data) => {
	emit('create-goal', data);
};

const submitGoal = (g) => {
	const name = (g?.name || '').trim();

	if (g?.id) {
		updateGoal({
			id: g.id,
			data: {
				name: g.name,
			},
		});
		return;
	}

	if (!name) {
		return;
	}

	createGoal({
		name,
		main_layer_id: props.data.id,
		parent_id: props.data.parent_id,
	});
	g.name = '';
};
</script>
<template>
	<div class="w-full overflow-x-auto mb-5 md-down:overflow-x-hidden">
		<table
			class="w-full table-fixed divide-y text-neutral-200 border-table bg-white"
		>
			<colgroup>
				<col class="w-20 md-down:w-12" />
				<col class="w-14 md-down:w-8" />
				<col />
				<col v-if="props.isInCreateGoal" class="w-28" />
				<col v-if="props.isEdit" class="w-24" />
			</colgroup>
			<thead class="bg-neutral-100 h-16">
				<tr>
					<th
						scope="col"
						class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider w-16 whitespace-nowrap md-down:px-2 md-down:w-12"
					></th>
					<th
						scope="col"
						class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider w-16 whitespace-nowrap md-down:px-1 md-down:w-8"
					>
						STT
					</th>
					<th
						scope="col"
						class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap md-down:px-3"
					>
						Mục tiêu cụ thể
					</th>
					<th
						v-if="props.isInCreateGoal"
						scope="col"
						class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
					></th>
					<th
						v-if="props.isEdit"
						scope="col"
						class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
					></th>
				</tr>
			</thead>
			<tbody style="border-top: none">
				<tr
					v-for="(g, i) in listSubItems"
					:key="g.id || `new-${i}`"
					class="bg-white text-neutral-700 border-table"
					:class="{ 'opacity-50': g.has_completed_goal }"
				>
					<td class="px-6 py-4 align-middle md-down:px-2">
						<div class="flex items-center gap-4 md-down:gap-1">
							<img
								class="w-4 h-4"
								src="@/assets/images/icons/child-goal.png"
								alt=""
							/>
							<Checkbox
								v-if="!props.groupCreate"
								:disabledAction="isGoalCheckboxDisabled(g)"
								:value="checkStatus(g)"
								@click="
									!isGoalCheckboxDisabled(g) &&
										checkChanged([g], false)
								"
							/>
						</div>
					</td>
					<td
						class="px-6 py-4 whitespace-nowrap text-neutral-700 md-down:px-1"
					>
						<div class="ml-2 md-down:ml-0">{{ i + 1 }}</div>
					</td>
					<td class="px-6 py-4 text-neutral-700 min-w-0 md-down:px-3">
						<div v-if="props.isEdit">
							<div
								class="input-group flex items-center w-full min-w-0"
							>
								<input
									type="text"
									placeholder="Điền tên mục tiêu mới"
									v-model="g.name"
									class="w-full min-w-0"
									@blur="submitGoal(g)"
									@keyup.enter.prevent="submitGoal(g)"
								/>
							</div>
						</div>
						<div v-else>
							<div class="w-full flex items-start gap-4 min-w-0">
								<div
									class="min-w-0 flex-1 break-words"
									v-html="g?.name"
								></div>
								<TooltipGoalDetail
									v-if="
										!g.sub_item_count &&
										(g.reason || g.strategy || g.process)
									"
									class="shrink-0"
									:tooltip-data="{
										reason: g.reason,
										strategy: g.strategy,
										process: g.process,
									}"
								/>
							</div>
							<div
								class="mt-1 py-0.5 px-2.5 rounded-full bg-yellow-tag text-neutral-700 h-fit w-fit font-semibold shadow-input whitespace-nowrap"
								style="font-size: 10px"
								v-if="g?.age"
							>
								{{ g?.age }}
							</div>
							<div
								v-if="g?.used || goalTaught.includes(g?.id)"
								class="flex gap-2 mt-1"
							>
								<svg
									width="16"
									height="16"
									viewBox="0 0 16 16"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M8.96966 4.96967C9.26256 4.67678 9.73743 4.67678 10.0303 4.96967C10.3196 5.25897 10.3232 5.72582 10.041 6.01947L6.04876 11.0097C6.043 11.0169 6.03685 11.0238 6.03032 11.0303C5.73743 11.3232 5.26256 11.3232 4.96966 11.0303L2.32322 8.38388C2.03032 8.09099 2.03032 7.61612 2.32322 7.32322C2.61611 7.03033 3.09098 7.03033 3.38388 7.32322L5.4774 9.41674L8.94977 4.9921C8.95595 4.98424 8.96259 4.97674 8.96966 4.96967Z"
										fill="#2AA797"
									/>
									<path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M8.04924 10.1099L8.96966 11.0303C9.26256 11.3232 9.73743 11.3232 10.0303 11.0303C10.0368 11.0238 10.043 11.0169 10.0488 11.0097L14.041 6.01947C14.3232 5.72582 14.3196 5.25897 14.0303 4.96967C13.7374 4.67678 13.2626 4.67678 12.9697 4.96967C12.9626 4.97674 12.9559 4.98424 12.9498 4.9921L9.4774 9.41674L8.99205 8.9314L8.04924 10.1099Z"
										fill="#2AA797"
									/>
								</svg>
								<span
									v-if="g?.used_at"
									class="font-semibold text-green-500 text-xs"
									>{{ $filters.formatDate(g?.used_at) }}</span
								>
								<span
									v-else-if="goalTaught.includes(g?.id)"
									class="font-semibold text-green-500 text-xs"
									>{{
										dayjs(Date.now()).format('DD/MM/YYYY')
									}}</span
								>
							</div>
						</div>
					</td>
					<td class="w-14" v-if="props.isInCreateGoal">
						<div
							v-if="g.has_completed_goal"
							class="flex items-center gap-2 whitespace-nowrap"
						>
							<img
								src="@/assets/images/icons/green-tick.png"
								alt=""
								class="w-4 h-4"
							/>
							<div class="font-semibold text-green-500 text-xs">
								HOÀN THÀNH
							</div>
						</div>
						<div v-else>
							<div
								v-if="
									(goalTaught.includes(g.id) || g.used) &&
									props.formCreate
								"
								class="font-semibold text-green-500 text-xs"
							>
								Đã thêm
							</div>
							<div
								v-else
								@click="emit('action-clicked', g)"
								class="px-1 cursor-pointer text-yellow-600 hover:text-yellow-700 font-semibold text-sm flex items-center md-down:items-right"
							>
								Thêm MT
							</div>
						</div>
					</td>
					<td class="w-24" v-if="props.isEdit && g.id">
						<div class="flex items-center gap-6">
							<div
								@click="
									deleteGoal({
										itemId: g.id,
										isGoal: true,
									})
								"
								class="text-yellow-600 cursor-pointer hover:text-yellow-700"
							>
								<svg
									width="20"
									height="20"
									viewBox="0 0 20 20"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M3.125 1.25024C2.43464 1.25024 1.875 1.80989 1.875 2.50024V3.75024C1.875 4.4406 2.43464 5.00024 3.125 5.00024H3.75V16.2502C3.75 17.631 4.86929 18.7502 6.25 18.7502H13.75C15.1307 18.7502 16.25 17.631 16.25 16.2502V5.00024H16.875C17.5654 5.00024 18.125 4.4406 18.125 3.75024V2.50024C18.125 1.80989 17.5654 1.25024 16.875 1.25024H12.5C12.5 0.559888 11.9404 0.000244141 11.25 0.000244141H8.75C8.05964 0.000244141 7.5 0.559888 7.5 1.25024H3.125ZM6.875 6.25024C7.22018 6.25024 7.5 6.53007 7.5 6.87524V15.6252C7.5 15.9704 7.22018 16.2502 6.875 16.2502C6.52982 16.2502 6.25 15.9704 6.25 15.6252L6.25 6.87524C6.25 6.53007 6.52982 6.25024 6.875 6.25024ZM10 6.25024C10.3452 6.25024 10.625 6.53007 10.625 6.87524V15.6252C10.625 15.9704 10.3452 16.2502 10 16.2502C9.65482 16.2502 9.375 15.9704 9.375 15.6252V6.87524C9.375 6.53007 9.65482 6.25024 10 6.25024ZM13.75 6.87524C13.75 6.53007 13.4702 6.25024 13.125 6.25024C12.7798 6.25024 12.5 6.53007 12.5 6.87524V15.6252C12.5 15.9704 12.7798 16.2502 13.125 16.2502C13.4702 16.2502 13.75 15.9704 13.75 15.6252V6.87524Z"
										fill="currentColor"
									/>
								</svg>
							</div>
							<div
								@click="openAddSmartModal(g)"
								class="text-yellow-600 cursor-pointer hover:text-yellow-700"
							>
								<svg
									width="20"
									height="20"
									viewBox="0 0 20 20"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M15.4041 8.49733L17.1377 6.76375L17.1377 6.76374L17.1377 6.76372C17.6368 6.26463 17.8863 6.01508 18.0269 5.74991C18.3376 5.164 18.3376 4.46221 18.0269 3.8763C17.8863 3.61112 17.6368 3.36156 17.1377 2.86246C16.6386 2.36335 16.389 2.11379 16.1238 1.97321C15.5379 1.66258 14.8361 1.66258 14.2502 1.97321C13.985 2.11379 13.7355 2.36335 13.2364 2.86246L11.4807 4.61818C12.4198 6.22874 13.772 7.57058 15.4041 8.49733ZM10.0262 6.07265L3.36419 12.7346C2.93913 13.1597 2.7266 13.3722 2.58687 13.6333C2.44713 13.8944 2.38819 14.1891 2.27029 14.7786L1.67957 17.7322C1.61305 18.0648 1.57979 18.2311 1.6744 18.3257C1.76901 18.4203 1.93531 18.3871 2.26792 18.3206L5.22152 17.7298C5.81098 17.6119 6.1057 17.553 6.3668 17.4133C6.6279 17.2735 6.84043 17.061 7.26549 16.6359L13.9459 9.95556C12.3647 8.96432 11.027 7.63576 10.0262 6.07265Z"
										fill="currentColor"
									/>
								</svg>
							</div>
						</div>
					</td>
				</tr>
			</tbody>
		</table>
	</div>
</template>
<style scoped>
.border-table {
	border: 1px solid #f1f5f9;
	border-collapse: collapse;
}
</style>
